import { reduceErrors } from '@/utils/errors';

import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { ERROR, SHIPPING_ERRORS } from '@/constants/product';
import { ATTRIBUTE } from '@/constants/attributes';
import { FEATURE } from '@/constants/features';
import { EMPTY } from '@/constants';
import { FORM } from '@/constants/forms';

export function formHasErrors({ product, type, userFeatures }) {
  function reduceShippingErrors(result, attribute) {
    return result || product.hasIn([FORM.SHIPPING, attribute, ERROR]);
  }

  switch (product.get('channel')) {
    case EBAY: {
      switch (type) {
        case FORM.DESCRIPTION: {
          return product.hasIn([FORM.DESCRIPTION, ERROR]);
        }

        case FORM.DETAILS: {
          return (
            product.hasIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, ERROR]) ||
            product.hasIn([FORM.DETAILS, ATTRIBUTE.CONDITION, ERROR]) ||
            product.hasIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, ERROR])
          );
        }

        case FORM.INVENTORY: {
          return (
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, ERROR]) ||
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.SKU, ERROR])
          );
        }

        case FORM.PHOTOS: {
          return product.hasIn([FORM.PHOTOS, ERROR]);
        }

        case FORM.PRICING: {
          return (
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.PRICE, ERROR]) ||
            product.hasIn([FORM.SHIPPING, ATTRIBUTE.PAYMENT_POLICY, ERROR])
          );
        }

        case FORM.SHIPPING: {
          return [
            ATTRIBUTE.COUNTRY,
            ATTRIBUTE.LOCATION,
            ATTRIBUTE.POSTAL_CODE,
            ATTRIBUTE.RETURN_POLICY,
            ATTRIBUTE.SHIPPING_POLICY,
            ATTRIBUTE.DEPTH,
            ATTRIBUTE.LENGTH,
            ATTRIBUTE.WIDTH,
            ATTRIBUTE.WEIGHT_MAJOR,
            ATTRIBUTE.WEIGHT_MINOR,
          ].reduce(reduceShippingErrors, false);
        }

        case FORM.TITLE: {
          return product.hasIn([FORM.TITLE, ERROR]);
        }

        case FORM.VARIATIONS: {
          return reduceErrors(product.getIn([FORM.VARIATIONS, ERROR], EMPTY.MAP));
        }

        default: {
          return false;
        }
      }
    }

    case ETSY: {
      switch (type) {
        case FORM.DESCRIPTION: {
          return product.hasIn([FORM.DESCRIPTION, ERROR]);
        }

        case FORM.DETAILS: {
          return (
            product.hasIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, ERROR]) ||
            product.hasIn([FORM.DETAILS, ATTRIBUTE.IS_SUPPLY, ERROR]) ||
            product.hasIn([FORM.DETAILS, ATTRIBUTE.PRODUCTION_PARTNERS, ERROR]) ||
            product.hasIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES, ERROR]) ||
            product.hasIn([FORM.DETAILS, ATTRIBUTE.WHEN_MADE, ERROR]) ||
            product.hasIn([FORM.DETAILS, ATTRIBUTE.WHO_MADE, ERROR])
          );
        }

        case FORM.FILES: {
          return product.hasIn([FORM.FILES, ERROR]);
        }

        case FORM.INVENTORY: {
          return (
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, ERROR]) ||
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.SKU, ERROR])
          );
        }

        case FORM.PERSONALIZATION: {
          return userFeatures.get(FEATURE.ETSY_PERSONALIZATION_QUESTIONS)
            ? reduceErrors(product.getIn([FORM.PERSONALIZATION, ERROR], EMPTY.MAP))
            : (
              product.hasIn([FORM.PERSONALIZATION, ATTRIBUTE.CHAR_COUNT_MAX, ERROR]) ||
              product.hasIn([FORM.PERSONALIZATION, ATTRIBUTE.INSTRUCTIONS, ERROR])
            );
        }

        case FORM.PHOTOS: {
          return product.hasIn([FORM.PHOTOS, ERROR]);
        }

        case FORM.PRICING: {
          return product.hasIn([FORM.INVENTORY, ATTRIBUTE.PRICE, ERROR]);
        }

        case FORM.SHIPPING: {
          return SHIPPING_ERRORS[ETSY].reduce(reduceShippingErrors, false);
        }

        case FORM.TAGS: {
          return product.hasIn([FORM.TAGS, ERROR]);
        }

        case FORM.TITLE: {
          return product.hasIn([FORM.TITLE, ERROR]);
        }

        case FORM.VARIATIONS: {
          return (
            !!product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, ERROR]) ||
            reduceErrors(product.getIn([FORM.VARIATIONS, ERROR], EMPTY.MAP))
          );
        }

        default: {
          return false;
        }
      }
    }

    case FAIRE: {
      switch (type) {
        case FORM.DESCRIPTION: {
          return product.hasIn([FORM.DESCRIPTION, ERROR]);
        }

        case FORM.PHOTOS: {
          return product.hasIn([FORM.PHOTOS, ERROR]);
        }

        case FORM.PRICING: {
          return (
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.RETAIL_PRICE, ERROR]) ||
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.WHOLESALE_PRICE, ERROR])
          );
        }

        case FORM.SHIPPING: {
          return SHIPPING_ERRORS[FAIRE].reduce(reduceShippingErrors, false);
        }

        case FORM.TITLE: {
          return product.hasIn([FORM.TITLE, ERROR]);
        }

        case FORM.VARIATIONS: {
          return reduceErrors(product.getIn([FORM.VARIATIONS, ERROR], EMPTY.MAP));
        }

        default: {
          return false;
        }
      }
    }

    case SHOPIFY: {
      switch (type) {
        case FORM.DESCRIPTION: {
          return product.hasIn([FORM.DESCRIPTION, ERROR]);
        }

        case FORM.INVENTORY: {
          return (
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.BARCODE, ERROR]) ||
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.QUANTITY, ERROR]) ||
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.SKU, ERROR])
          );
        }

        case FORM.PHOTOS: {
          return product.hasIn([FORM.PHOTOS, ERROR]);
        }

        case FORM.PRICING: {
          return (
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.CAP, ERROR]) ||
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.CPI, ERROR]) ||
            product.hasIn([FORM.INVENTORY, ATTRIBUTE.PRICE, ERROR])
          );
        }

        case FORM.SHIPPING: {
          return product.hasIn([FORM.INVENTORY, ATTRIBUTE.WEIGHT, ERROR]);
        }

        case FORM.TAGS: {
          return product.hasIn([FORM.TAGS, ERROR]);
        }

        case FORM.TITLE: {
          return product.hasIn([FORM.TITLE, ERROR]);
        }

        case FORM.VARIATIONS: {
          return reduceErrors(product.getIn([FORM.VARIATIONS, ERROR], EMPTY.MAP));
        }

        default: {
          return false;
        }
      }
    }

    default: {
      return false;
    }
  }
}
